import React from 'react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  loading?: boolean;
}

const variantStyles: Record<string, React.CSSProperties> = {
  primary: {
    background: 'var(--blue-600)',
    color: 'var(--neutral-100)',
  },
  secondary: {
    background: 'var(--neutral-100)',
    color: 'var(--neutral-800)',
    border: '1px solid var(--neutral-300)',
  },
  ghost: {
    background: 'transparent',
    color: 'var(--neutral-700)',
  },
  danger: {
    background: 'var(--danger-600)',
    color: 'var(--neutral-100)',
  },
};

const sizeStyles: Record<string, React.CSSProperties> = {
  sm: { padding: '6px 12px', fontSize: 13, height: 32 },
  md: { padding: '10px 20px', fontSize: 14, height: 40 },
  lg: { padding: '12px 24px', fontSize: 15, height: 48 },
};

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  loading = false,
  disabled,
  children,
  style,
  ...rest
}) => {
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      style={{
        ...variantStyles[variant],
        ...sizeStyles[size],
        width: fullWidth ? '100%' : 'auto',
        borderRadius: 'var(--radius-md)',
        fontWeight: 600,
        fontFamily: 'var(--font-family)',
        cursor: disabled || loading ? 'not-allowed' : 'pointer',
        opacity: disabled || loading ? 0.6 : 1,
        transition: 'all 150ms ease',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        ...style,
      }}
      onMouseEnter={(e) => {
        if (!disabled && !loading) {
          if (variant === 'primary') e.currentTarget.style.background = 'var(--blue-700)';
          if (variant === 'secondary') e.currentTarget.style.background = 'var(--neutral-200)';
          if (variant === 'ghost') e.currentTarget.style.background = 'var(--neutral-200)';
        }
      }}
      onMouseLeave={(e) => {
        if (!disabled && !loading) {
          e.currentTarget.style.background = variantStyles[variant].background as string;
        }
      }}
    >
      {loading ? 'Loading…' : children}
    </button>
  );
};